/* eslint-disable camelcase */
import PageBox from "PFComponents/box/page_box";
import ButtonLink from "PFComponents/button/button_link";
import DataTable from "PFComponents/data_table/data_table";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import { PaginatedCollection } from "PFTypes";
import { SamlConfig } from "PFTypes/saml_config";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { activateSamlConfig, deleteSamlConfig } from "../services/saml_configs_api";
import SamlConfigRowActions from "./parts/saml_config_row_actions";
import css from "./saml_config_page.module.scss";

type SamlConfigsPageProps = {
  token: string;
  samlConfigs: PaginatedCollection<SamlConfig[]>;
  fetchSamlConfigs: (page?: number) => void;
};

const SamlConfigsPage = ({ samlConfigs, token, fetchSamlConfigs }: SamlConfigsPageProps): JSX.Element => {
  const { t: defaultT } = useTranslation();
  const { t } = useTranslation("samlConfigs", { keyPrefix: "list" });

  const renderIdProviderCell = useCallback(
    ({ idp_slo_service_url, idp_sso_service_url }: SamlConfig): JSX.Element => (
      <span className={css.cell}>{idp_slo_service_url || idp_sso_service_url}</span>
    ),
    []
  );

  const renderActionsCell = useCallback(
    ({ id, active, idp_slo_service_url, idp_sso_service_url }: SamlConfig): JSX.Element => (
      <SamlConfigRowActions
        id={id}
        active={active}
        onActivate={() => {
          activateSamlConfig(id).then(() => fetchSamlConfigs(samlConfigs?.meta?.page));
        }}
        onDelete={() => {
          if (confirm(defaultT("sureToDelete", { key: idp_slo_service_url || idp_sso_service_url }))) {
            deleteSamlConfig(id).then(() => fetchSamlConfigs(samlConfigs?.meta?.page));
          }
        }}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchSamlConfigs, samlConfigs?.meta?.page]
  );

  return (
    <div className={css.wrapper}>
      <div className={css.header}>
        <h1>{t("samlConfigs")}</h1>
        <ButtonLink href="/admin/saml_configs/new">{t("newSamlConfig")}</ButtonLink>
      </div>
      <div className={css.tokenWrapper}>
        {t("description.part1")}
        <span className={css.token}>{t("description.token", { token })}</span>
        {t("description.part2")}
      </div>
      <PageBox>
        {samlConfigs?.meta?.total ? (
          <DataTable
            style={{ whiteSpace: "nowrap" }}
            columns={[
              {
                headerName: t("headers.idProviderHeader"),
                renderCell: renderIdProviderCell
              },
              {
                headerName: t("headers.actions"),
                renderCell: renderActionsCell,
                narrow: true
              }
            ]}
            data={samlConfigs?.entries}
          />
        ) : (
          <div className={css.notFound}>{t("emailTemplatesNotFound")}</div>
        )}
      </PageBox>
      <div className={css.pagination}>
        <SimplePagination
          currentPage={samlConfigs?.meta?.page}
          totalPages={samlConfigs?.meta?.totalPages}
          handleClick={fetchSamlConfigs}
        />
      </div>
    </div>
  );
};

export default SamlConfigsPage;
